import { computed, type Ref } from "vue";
// Single source of truth for platform-login detection / gating / status lives in the shared
// realtime-analysis package (consumed by contact-async + contact-live). contact-admin now wraps it
// too — via the existing `@libs` → shared/frontend source alias (the same way @common-ui is
// consumed) — so the run, queue, and template-edit flows share one implementation instead of a
// divergent copy. We import the composable subpath directly (not the package barrel) to avoid
// dragging in the SDK/auth0 surface we don't need. Transport is injected below and routed through
// scribe (not the legacy Exchange service), matching the realtime apps.
import { usePackRequirements, type PackTask } from "@libs/realtime-analysis/src/composables/usePackRequirements";
import { getAvailablePlatforms, getPlatformLoginUrl as scribeGetPlatformLoginUrl, logoutPlatform as scribeLogoutPlatform } from "@/api/scribe";
import { useAuthSessionStore } from "@/stores/authSession";
import type { RunAgentTask, PlatformRequirement } from "../types";

export type { PlatformRequirement };

export interface UseRunAgentPlatformsOptions {
	loadedPackTasks: Ref<RunAgentTask[]>;
}

export function useRunAgentPlatforms(options: UseRunAgentPlatformsOptions) {
	const { loadedPackTasks } = options;

	const requirements = usePackRequirements({
		// RunAgentTask is structurally compatible with the shared PackTask for the fields the
		// composable reads (type, platform, name, inlineFormConfig.fields[].mcpDataSource).
		loadedPackTasks: loadedPackTasks as unknown as Ref<PackTask[]>,
		// Resolve the org lazily: only login/logout need it, so we avoid requiring an active
		// Pinia at composable-creation time (keeps detection/gating usable in plain unit setups).
		getOrganizationId: () => useAuthSessionStore().organizationId ?? "",
		getPlatformLoginUrl: async (codeName, redirectUri, organizationId) => scribeGetPlatformLoginUrl(codeName, redirectUri, organizationId),
		logoutPlatform: (codeName, organizationId) => scribeLogoutPlatform(codeName, organizationId),
		fetchPlatforms: (fetchOptions) => getAvailablePlatforms(fetchOptions)
	});

	// Admin-only extras: per-task disable state consumed by the task-selection UI (TaskSelectionCore.vue).
	// A platform task whose platform is not yet authenticated is shown disabled with a reason. Both
	// outputs come from a single pass over the tasks so the loop + lookup isn't duplicated.
	const disabledTasks = computed(() => {
		const ids: string[] = [];
		const reasons: Record<string, string> = {};
		for (const task of loadedPackTasks.value) {
			if (task.type !== "platform" || !task.platform) continue;
			const requirement = requirements.platformRequirements.value.find((platform: PlatformRequirement) => platform.codeName === task.platform);
			if (requirement && requirement.requiresAuth && requirement.authStatus !== "Authenticated") {
				ids.push(task.id);
				reasons[task.id] = `Requires ${requirement.productName} login`;
			}
		}
		return { ids, reasons };
	});

	const disabledTaskIds = computed(() => disabledTasks.value.ids);
	const disabledReasons = computed(() => disabledTasks.value.reasons);

	return {
		...requirements,
		// Re-assert the element type: the shared composable is consumed via the `@libs` source
		// alias (not a typed package entry), so its inferred return widens to `any` under the app's
		// vue-tsc — the same way @common-ui source is consumed. Pin the one member that callers
		// iterate so RunAgentDialog / TemplateEditPage keep concrete `PlatformRequirement` typing.
		platformRequirements: requirements.platformRequirements as unknown as Ref<PlatformRequirement[]>,
		disabledTaskIds,
		disabledReasons
	};
}
